"use client";

import { useEffect, useState } from "react";
import { useStore } from "@/stores/appStore";
import { UploadItem } from "../core/types";

const EMPTY_ARRAY: UploadItem[] = [];

export function useUploadSession(route?: string, subjectId?: string) {
  const getOrCreateUploadSession = useStore((state) => state.getOrCreateUploadSession);
  const addUploadTasks = useStore((state) => state.addUploadTasks);
  const updateUploadTask = useStore((state) => state.updateUploadTask);
  const removeUploadTask = useStore((state) => state.removeUploadTask);
  const clearUploadSession = useStore((state) => state.clearUploadSession);
  const uploadSessions = useStore((state) => state.uploadSessions);

  // 初始化 sessionId 为 null，避免在渲染期间触发状态更新
  const [sessionId, setSessionId] = useState<string | null>(null);

  // 允许外部在清空旧任务后强制创建/获取一个新的 sessionId
  const refreshSession = (overrideRoute?: string, overrideSubjectId?: string) => {
    const fallbackRoute = typeof window !== "undefined" ? window.location.pathname : "";
    const targetRoute = overrideRoute ?? route ?? fallbackRoute;
    const targetSubjectId = overrideSubjectId ?? subjectId;
    const id = getOrCreateUploadSession(targetRoute, targetSubjectId);
    if (!id) {
      console.error("refreshSession: getOrCreateUploadSession returned empty id");
      return "";
    }
    setSessionId(id);
    return id;
  };

  // 在 useEffect 中初始化或更新 sessionId，避免在渲染期间触发状态更新
  useEffect(() => {
    const fallbackRoute = typeof window !== "undefined" ? window.location.pathname : "";
    const id = getOrCreateUploadSession(route ?? fallbackRoute, subjectId);
    setSessionId(id);
  }, [getOrCreateUploadSession, route, subjectId]);

  // 监听 uploadSessions 变化，如果当前 sessionId 对应的 session 不存在了，重新获取或创建 session
  useEffect(() => {
    if (sessionId && !uploadSessions[sessionId]) {
      // session 不存在了，重新获取或创建
      const fallbackRoute = typeof window !== "undefined" ? window.location.pathname : "";
      const id = getOrCreateUploadSession(route ?? fallbackRoute, subjectId);
      setSessionId(id);
    }
  }, [sessionId, uploadSessions, getOrCreateUploadSession, route, subjectId]);

  const tasks = useStore((state) =>
    sessionId ? (state.uploadSessions[sessionId]?.tasks ?? EMPTY_ARRAY) : EMPTY_ARRAY
  );

  return {
    sessionId: sessionId ?? "",
    tasks,
    addUploadTasks,
    updateUploadTask,
    removeUploadTask,
    clearUploadSession,
    refreshSession,
  };
}

